import React, { useRef, useEffect, useState, KeyboardEvent } from 'react';

import {
  Container,
  Row,
  Button,
  Dropdown,
  Modal,
  Form,
  Spinner,
  Image,
} from 'react-bootstrap';

import { Editor, EditorProps } from 'react-draft-wysiwyg';
import {
  EditorState,
  convertToRaw,
  ContentState,
  convertFromHTML,
  ContentBlock,
  Modifier,
  BlockMap,
  BlockMapBuilder,
} from 'draft-js';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import draftToHtml from 'draftjs-to-html';
import * as Yup from 'yup';

import ListUserStatus from './ListUserStatus';
import ImageListClipboard from './ImageListClipboard';
import { Message, MessageGeneric } from '../../interfaces/IMessage';
import { userStatus as enumUserStatus } from '../../enums/userStatus';

import AnexoIcon from '../../assets/icon/anexo.svg';
import Boldicon from '../../assets/icon/bold.svg';
import ItalicIcon from '../../assets/icon/italic.svg';
import UnderlineIcon from '../../assets/icon/underline.svg';
import MenuIcon from '../../assets/icon/menu.svg';

import api from '../../services/apiClient';
import { useAuth } from '../../hooks/auth';
import { useMessage } from '../../hooks/message';
import { useToast } from '../../hooks/toast';
import { useUserStatus } from '../../hooks/userStatus';
import { useImageClipboard } from '../../hooks/imageClipboard';
import IMensagemPadrao from '../../interfaces/IMensagemPadrao';

interface MessageChatCache {
  seqChat: number;
  message: EditorState | null;
}

const SendMessage: React.FC<any> = ({ idSupportRequest, seqGrupoConversa }) => {
  const { user, hubConnection } = useAuth().dataConnection;
  const {
    getMensages,
    getMensagesGroup,
    clientsAccepted,
    editMessage,
    setEditMessage,
    citarMessage,
    setCitarMessage,
    setMessageChatCache,
    messageChatCache,
  } = useMessage();

  const { addToast } = useToast();
  const { userStatus } = useUserStatus();
  const { imageFile, removeImage, addImageClipboard } = useImageClipboard();

  const [showModalCloseChat, setShowModalCloseChat] = useState(false);
  const [showLoadingFile, setShowLoadingFile] = useState(false);
  const [showModalTrasfAtendimento, setShowModalTrasfAtendimento] =
    useState(false);
  const [showPopupMsgPadrao, setShowPopupMsgPadrao] = useState(false);
  const [textoMsgPadrao, setTextoMsgPadrao] = useState('');
  const [usuario, setUsuario] = useState(0);
  const [sendMessage, setSendMessage] = useState(false);
  const [messagePadrao, setMessagePadrao] = useState<IMensagemPadrao[]>([]);
  const [messagePadraoFiltro, setMessagePadraoFiltro] = useState<
    IMensagemPadrao[]
  >([]);

  const messageInput = useRef<Editor>(null);
  const bntSendMessage = useRef<HTMLButtonElement>(null);
  const inputFile = useRef<HTMLInputElement>(null);
  const bntTransferir = useRef<HTMLButtonElement>(null);
  const editorContainerRef = useRef<HTMLDivElement | null>(null);

  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  const disableButtonSend = (): void => {
    const text = draftToHtml(convertToRaw(editorState.getCurrentContent()));

    if (bntSendMessage.current !== null) {
      bntSendMessage.current.disabled =
        text.length === 8 && imageFile.length === 0;
    }
  };

  const atendimentoEmAndamento = 2;
  const atendimentoFinalizadoSuporte = 4;
  const dataClient = clientsAccepted.find(
    (client) => client.seqAtendimento === idSupportRequest
  );

  const handleLoadMesagePadrao = (): void => {
    const token = localStorage.getItem('@LiveChat:token');
    const seqEmpresa = localStorage.getItem('@LiveChat:seqEmpresa');

    api
      .get(`/Parametro/MensagemPadrao/${seqEmpresa}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.data.success) {
          setMessagePadrao(response.data.data);
        }
      });
  };

  useEffect(() => {
    messageInput.current?.focusEditor();

    messageInput.current?.setState({
      editorState: EditorState.createEmpty(),
    });
    handleLoadMesagePadrao();

    const chatCache = messageChatCache.find(
      (item) =>
        item.seqChat ===
        (seqGrupoConversa > 0 ? seqGrupoConversa : idSupportRequest)
    );

    if (chatCache !== undefined) {
      setEditorState(chatCache.message || EditorState.createEmpty());
    }
  }, [idSupportRequest, seqGrupoConversa]);

  useEffect(() => {
    disableButtonSend();
  }, [editorState, imageFile]);

  useEffect(() => {
    setEditMessage({} as MessageGeneric);
    setCitarMessage({} as MessageGeneric);

    // Adiciona o evento Past, para tratar imagens que estão na memória do Clipboard.
    document.addEventListener('paste', addImageClipboard);

    // Função para desativar o autocomplete
    function disableAutoComplete(): void {
      const inputElements = document.querySelectorAll('input');
      inputElements.forEach((input) => {
        input.setAttribute('autocomplete', 'off');
      });
    }

    // Chame a função ao carregar a página
    disableAutoComplete();
  }, []);

  useEffect(() => {
    const objVazio = {} as MessageGeneric;
    if (editMessage !== objVazio && editMessage.texto) {
      // 1. Substituir <br> em parágrafos vazios por espaço não quebrável
      const modifiedHTML = editMessage.texto
        .replaceAll('<ins>', '<i>')
        .replaceAll('</ins>', '</i>')
        .replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '<br />'); // Regex aprimorada

      // 2. Converter HTML normalmente
      const blocksFromHTML = convertFromHTML(modifiedHTML);

      // 3. Processar blocos para manter quebras
      const processedBlocks = blocksFromHTML.contentBlocks.map((block) => {
        const text = block.getText();
        // Restaurar quebras de linha se o bloco contiver apenas espaço
        return text === ' '
          ? new ContentBlock({
              ...block,
              text: '\n',
              characterList: block.getCharacterList(),
            })
          : block;
      });

      // 4. Criar ContentState processado
      const contentState = ContentState.createFromBlockArray(
        processedBlocks,
        blocksFromHTML.entityMap
      );

      // 5. Atualizar editor
      messageInput.current?.setState({
        editorState: EditorState.createWithContent(contentState),
      });

      messageInput.current?.focusEditor();
    }
  }, [editMessage]);

  const handlePaste: EditorProps['handlePastedText'] = (
    text,
    _html,
    editorStatePaste,
    onChange
  ) => {
    try {
      // 1) Texto puro do clipboard, limpando NBSP e normalizando quebras
      const cleaned = text
        .replace(/\u00A0/g, ' ')
        .replace(/\r\n/g, '\n')
        .split('\n')
        .map((l) => l.trim())
        .filter((l) => l.length > 0)
        .join('\n');

      // 2) Gera ContentState só com texto (cada \n vira um bloco)
      const fragmentContent = ContentState.createFromText(cleaned);

      // 3) Substitui a seleção atual pelo “fragment”
      const fragment = BlockMapBuilder.createFromArray(
        fragmentContent.getBlocksAsArray()
      );
      const newContent = Modifier.replaceWithFragment(
        editorStatePaste.getCurrentContent(),
        editorStatePaste.getSelection(),
        fragment
      );

      const newEditorState = EditorState.push(
        editorStatePaste,
        newContent,
        'insert-fragment'
      );

      onChange(
        EditorState.forceSelection(
          newEditorState,
          newContent.getSelectionAfter()
        )
      );
      return true; // impede o paste padrão
    } catch (e) {
      console.error('Erro ao processar colagem:', e);
      return false;
    }
  };

  useEffect(() => {
    messageInput.current?.focusEditor();
  }, [citarMessage]);

  const handleCloseChat = (): void => {
    const token = localStorage.getItem('@LiveChat:token');
    api
      .patch(
        `/SolicitacaoAtendimento?idSupportRequest=${idSupportRequest}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then(() => {
        setShowModalCloseChat(false);
        if (dataClient?.seqStatusAtendimento) {
          dataClient.seqStatusAtendimento = atendimentoFinalizadoSuporte;
        }
      });
  };

  const handleSendImage = (): void => {
    const formData = new FormData();
    formData.append('seqAtendimento', idSupportRequest);
    formData.append('seqGrupoConversa', seqGrupoConversa);

    for (let i = 0; i < imageFile.length; i += 1) {
      formData.append(`file[${i}]`, imageFile[i].File);
    }

    formData.append('idUser', user);
    formData.append('nameClient', user);
    formData.append(
      'idConnectionClient',
      hubConnection?.connectionId == null ? '' : hubConnection.connectionId
    );

    const token = localStorage.getItem('@LiveChat:token');

    api
      .post(`/api/Imagem`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((request) => {
        const { success, data } = request.data;

        if (success) {
          for (let i = 0; i < imageFile.length; i += 1) {
            removeImage(imageFile[i].key);
          }

          if (idSupportRequest > 0) {
            getMensages(idSupportRequest, data);
          } else {
            getMensagesGroup(seqGrupoConversa, data, 'N');
          }

          messageInput.current?.focusEditor();

          addToast({
            type: 'success',
            title: 'Sucesso',
            description: `O arquivo foi enviado com sucesso!`,
          });
          messageInput.current?.focusEditor();
        }
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Falha ao enviar arquivo',
        });
      });
  };

  const handleSendFile = (): void => {
    if (inputFile.current === null || inputFile.current === undefined) return;
    if (
      inputFile.current.files === null ||
      inputFile.current.files === undefined
    )
      return;

    const file = inputFile.current?.files[0];

    if (file) {
      setShowLoadingFile(true);

      const formData = new FormData();
      formData.append('seqAtendimento', idSupportRequest);
      formData.append('seqGrupoConversa', seqGrupoConversa);
      formData.append('file', file);
      formData.append('idUser', user);
      formData.append('nameClient', user);
      formData.append(
        'idConnectionClient',
        hubConnection?.connectionId == null ? '' : hubConnection.connectionId
      );

      const token = localStorage.getItem('@LiveChat:token');

      api
        .post(`/api/ArquivoAnexo`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        })
        .then((request) => {
          const { success } = request.data;

          if (success) {
            addToast({
              type: 'success',
              title: 'Sucesso',
              description: `O arquivo ${file.name} foi enviado com sucesso!`,
            });
            messageInput.current?.focusEditor();
          }
        })
        .catch(() => {
          addToast({
            type: 'error',
            title: 'Falha ao enviar arquivo',
          });
        })
        .finally(() => {
          if (inputFile?.current !== null) inputFile.current.value = '';
          setShowLoadingFile(false);
        });
    }
  };

  const handleTransferChat = (): void => {
    if (usuario === 0) {
      addToast({
        type: 'error',
        title: 'Atenção',
        description:
          'Por favor, selecione um usuário para transferir o atendimento.',
      });
      return;
    }

    const dataTransfer = {
      seqAtendimento: idSupportRequest,
      seqUserDestino: usuario,
    };
    const token = localStorage.getItem('@LiveChat:token');

    if (bntTransferir.current !== null) bntTransferir.current.disabled = true;

    api
      .post('/TransferenciaAtendimento', dataTransfer, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        if (dataClient?.seqStatusAtendimento) {
          dataClient.seqStatusAtendimento = atendimentoFinalizadoSuporte;
        }
        addToast({
          type: 'success',
          title: 'Transferência realizada com sucesso',
        });
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Falha ao transferir atendimento',
        });
      })
      .finally(() => {
        setShowModalTrasfAtendimento(false);
      });
  };

  const handleTransferChatClick = (): void => {
    setUsuario(0);
    setShowModalTrasfAtendimento(true);
  };

  const convertUrlsToLinks = (text: string): string => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.replace(
      urlRegex,
      (url) =>
        `<a href="${url.replace('</p>', '')}" target="_blank">${url.replace(
          '</p>',
          ''
        )}</a>`
    );
  };

  function removeLastEmptyParagraph(message: string): string {
    let trimmedMessage = message.trim();

    if (trimmedMessage.endsWith('<p></p>')) {
      trimmedMessage = trimmedMessage.slice(0, -9);
    }

    return trimmedMessage.replace(/<p><\/p>(?!$)/g, '<p><br/></p>').trim();
  }

  const submitMessage = async (message: string): Promise<void> => {
    try {
      // Trata mensagens vazias
      let filteredMessage = removeLastEmptyParagraph(message);

      if (imageFile.length > 0) handleSendImage();

      if (filteredMessage.length === 0) return;

      filteredMessage = convertUrlsToLinks(filteredMessage);

      const schema = Yup.object().shape({
        filteredMessage: Yup.string()
          .required()
          .min(1, 'É necessário informar uma mensagem.')
          .max(
            4000,
            'A menssagem não pode possuir mais do que 4.000 caracteres.'
          ),
      });

      const objectToValidation = {
        filteredMessage,
      };

      await schema.validate(objectToValidation, { abortEarly: false });

      if (bntSendMessage.current !== null) {
        bntSendMessage.current.disabled = true;
      }

      const token = localStorage.getItem('@LiveChat:token');
      const seqUsuario = localStorage.getItem('@LiveChat:seqUsuario');
      let resultSendMessage;

      if (idSupportRequest > 0) {
        const dataRequest = {
          idSupportRequest,
          text: filteredMessage,
          idUser: user,
          nameClient: user,
          idConnectionClient: hubConnection?.connectionId,
          seqMensagemOrigem: editMessage.seqMensagem,
          seqCitarMensagem: citarMessage.seqMensagem,
        };

        resultSendMessage = await api.post(
          'api/AtendimentoMensagem',
          dataRequest,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }

      if (seqGrupoConversa > 0) {
        const dataRequest = {
          seqGrupoConversa,
          texto: filteredMessage,
          seqUsuario,
          seqMensagemOrigem: editMessage.seqMensagem,
          seqMensagemCitacao: citarMessage.seqMensagem,
        };

        resultSendMessage = await api.post('api/MensagemGrupo', dataRequest, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      if (resultSendMessage === undefined) return;

      const { success, data } = resultSendMessage.data;

      if (success) {
        setEditMessage({} as MessageGeneric);
        setCitarMessage({} as MessageGeneric);
        messageInput.current?.setState({
          editorState: EditorState.createEmpty(),
        });
        setEditorState(EditorState.createEmpty());

        if (idSupportRequest > 0) {
          getMensages(idSupportRequest, data);
        } else {
          getMensagesGroup(seqGrupoConversa, data, 'N');
        }

        messageInput.current?.focusEditor();
      }

      if (bntSendMessage.current !== null) {
        bntSendMessage.current.disabled = false;
      }
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        console.log('Error:', err);
        addToast({
          type: 'error',
          title: 'Mensagem inválida',
          description: err.message,
        });
      } else console.log('Error:', err);
    } finally {
      setSendMessage(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key !== 'Enter' && e.key !== 'Escape' && idSupportRequest > 0) {
      const matchAtalho = messagePadrao.find(({ atalho }) => {
        return atalho === e.key;
      });

      if (matchAtalho) {
        setShowPopupMsgPadrao(true);
        setTextoMsgPadrao((texto) => texto + e.key);
      }
    }

    if (e.key === 'Enter' || e.key === 'Escape' || e.key === ' ') {
      setShowPopupMsgPadrao(false);
      setTextoMsgPadrao('');
    }

    if (showPopupMsgPadrao) {
      if (e.key === 'Backspace') {
        if (textoMsgPadrao.length === 1) {
          setShowPopupMsgPadrao(false);
          setTextoMsgPadrao('');
        } else setTextoMsgPadrao((texto) => texto.slice(0, -1));
      } else if (
        /^[a-zA-Z0-9çÇáàãâéèêíìîóòõôúùûÁÀÃÂÉÈÊÍÌÎÓÒÕÔÚÙÛ]$/.test(e.key)
      ) {
        setTextoMsgPadrao((texto) => texto + e.key);
      }

      if (textoMsgPadrao.length === 0) setShowPopupMsgPadrao(false);
    }

    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !sendMessage) {
      setSendMessage(true);
      // Timer Criado para evitar duplo 'Enter'
      setTimeout(() => {
        const contentState = editorState.getCurrentContent();
        const plainText = contentState.getPlainText('\n').trim();

        const textRaw = convertToRaw(contentState);
        for (let line = 0; line < textRaw.blocks.length; line += 1) {
          textRaw.blocks[line].inlineStyleRanges = textRaw.blocks[
            line
          ].inlineStyleRanges.filter(
            (itemStyle) =>
              itemStyle.style === 'BOLD' ||
              itemStyle.style === 'UNDERLINE' ||
              itemStyle.style === 'ITALIC'
          );
        }

        const matchAtalho = messagePadrao.find(
          ({ atalho, identificacao }) =>
            plainText === `${atalho}${identificacao}`
        );

        let htmlToSend = draftToHtml(textRaw);
        if (matchAtalho) {
          const textToReplace = `${matchAtalho.atalho}${matchAtalho.identificacao}`;
          const updatedHtml = htmlToSend.replace(
            textToReplace,
            matchAtalho.texto
          );

          const contentBlock = convertFromHTML(updatedHtml);
          const contentStateUpdate = ContentState.createFromBlockArray(
            contentBlock.contentBlocks
          );
          const editorStateToSend =
            EditorState.createWithContent(contentStateUpdate);
          const rawToSend = convertToRaw(editorStateToSend.getCurrentContent());
          htmlToSend = draftToHtml(rawToSend);
        }

        submitMessage(htmlToSend);
        setShowPopupMsgPadrao(false);
        setTextoMsgPadrao('');
      }, 50);
    } else if (e.key === 'Escape') {
      setEditMessage({} as MessageGeneric);
      setCitarMessage({} as MessageGeneric);
      messageInput.current?.setState({
        editorState: EditorState.createEmpty(),
      });
      setShowPopupMsgPadrao(false);
      setTextoMsgPadrao('');
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && (e.shiftKey || e.ctrlKey)) {
      const divsEditor = document.getElementsByClassName(
        // 'notranslate public-DraftEditor-content'
        'form-control overflow-auto cls-editor-send-mensage rdw-editor-main'
      );
      const divEditorSendMessage = divsEditor[divsEditor.length - 1];
      const { scrollHeight, clientHeight } = divEditorSendMessage;

      divEditorSendMessage.scrollTo({
        left: 0,
        top: scrollHeight - clientHeight,
        behavior: 'auto',
      });
    }

    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
      setMessageChatCache((objetoAtual: MessageChatCache[]) => {
        return objetoAtual.filter(
          (item) =>
            item.seqChat !==
            (seqGrupoConversa > 0 ? seqGrupoConversa : idSupportRequest)
        );
      });

      return;
    }

    setMessageChatCache((objetoAtual: MessageChatCache[]) => {
      const seqChat =
        seqGrupoConversa > 0 ? seqGrupoConversa : idSupportRequest;
      const index = objetoAtual.findIndex((item) => item.seqChat === seqChat);

      // Garantindo que seqGrupoConversa é do tipo number
      const novoObjeto: MessageChatCache = {
        seqChat,
        message: editorState,
      };

      if (index === -1) {
        return [...objetoAtual, novoObjeto];
      }

      return objetoAtual.map((item) => {
        if (item.seqChat === seqChat) {
          return { ...item, message: editorState };
        }
        return item;
      });
    });
  };

  const handleReturn = (event: KeyboardEvent): boolean => {
    if (event.key === 'Enter' && !event.shiftKey) {
      // Impede o comportamento padrão do Enter, que é adicionar um bloco de estilo
      event.preventDefault();
      return true; // Indica que tratamos o evento
    }
    return false; // Permite o comportamento padrão para outras teclas ou quando Shift está pressionado
  };

  const CustomOptionAnexo: React.FC<any> = (): any => {
    return (
      <div className="rdw-option-wrapper rounded">
        <button
          type="button"
          className="border-0 bg-white p-0 m-0 d-flex"
          onClick={() => {
            inputFile.current?.click();
          }}
        >
          <Image
            src={AnexoIcon}
            style={{ width: '1.0625rem', height: '1.0625rem' }}
          />
        </button>
      </div>
    );
  };

  const CustomLoadFile: React.FC<any> = (): any => {
    return (
      <div className="w-auto d-flex justify-content-between ms-2">
        {showLoadingFile && (
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <Spinner
                style={{ width: '1.2rem', height: '1.2rem' }}
                animation="border"
                variant="primary"
              />
            </div>
            <span
              className="ms-2 me-2 fw-bold text-info"
              style={{ fontSize: '1.2rem' }}
            >
              Enviando arquivo...
            </span>
          </div>
        )}
      </div>
    );
  };

  const CustomOptionChat: React.FC<any> = (): any => {
    return (
      idSupportRequest > 0 && (
        <div
          className="rdw-option-wrapper rdw-option-chat-wrapper rounded ps-2"
          style={{
            visibility: idSupportRequest === 0 ? 'hidden' : 'visible',
          }}
        >
          <Dropdown hidden={idSupportRequest === 0}>
            <Dropdown.Toggle variant="" id="dropdown-basic" size="sm">
              <Image
                style={{ width: '1rem', height: '1rem' }}
                src={MenuIcon}
                alt="Alterar"
              />
            </Dropdown.Toggle>

            <Dropdown.Menu className="p-1" style={{ fontSize: '1.25rem' }}>
              <Dropdown.Item
                className="bg-danger text-white text-center rounded mb-1"
                onClick={() => {
                  setShowModalCloseChat(true);
                }}
              >
                Encerrar o chat
              </Dropdown.Item>
              <Dropdown.Item
                className="bg-primary text-white text-center rounded"
                onClick={() => {
                  handleTransferChatClick();
                }}
              >
                Transferir atendimento
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      )
    );
  };

  const handleOnChange = (newEditorState: any): any => {
    const contentState = editorState.getCurrentContent();
    const rawContentState = convertToRaw(contentState);
    const textHtml = draftToHtml(rawContentState);

    const match = messagePadrao.find(({ atalho, identificacao }) =>
      textHtml.includes(`${atalho}${identificacao}`)
    );

    if (match) {
      const textToReplace = `${match.atalho}${match.identificacao}`;
      const updatedText = textHtml.replace(textToReplace, match.texto);

      const contentBlock = convertFromHTML(updatedText);
      const contentStateUpdate = ContentState.createFromBlockArray(
        contentBlock.contentBlocks
      );
      const newEditorStateWithContent =
        EditorState.createWithContent(contentStateUpdate);
      const editorStateWithFocus = EditorState.moveFocusToEnd(
        newEditorStateWithContent
      );
      setEditorState(editorStateWithFocus);

      return;
    }

    setEditorState(newEditorState);
  };

  const handleClickItemMsgPadrao = (texto: string): any => {
    const contentState = editorState.getCurrentContent();
    const rawContentState = convertToRaw(contentState);
    const textHtml = draftToHtml(rawContentState);
    const updatedText = textHtml.replace(textoMsgPadrao, texto);

    const contentBlock = convertFromHTML(updatedText);
    const contentStateUpdate = ContentState.createFromBlockArray(
      contentBlock.contentBlocks
    );
    const newEditorStateWithContent =
      EditorState.createWithContent(contentStateUpdate);
    const editorStateWithFocus = EditorState.moveFocusToEnd(
      newEditorStateWithContent
    );
    setEditorState(editorStateWithFocus);
    setShowPopupMsgPadrao(false);
    setTextoMsgPadrao('');
  };

  const handleWheel = (event: any): void => {
    // Impedir o comportamento padrão para controlar a rolagem
    event.preventDefault();

    // Quantidade personalizada de linhas a serem roladas
    const linesToScroll = 1;
    const scrollAmount = linesToScroll * 16; // 16px é a altura aproximada de uma linha padrão
    const container = editorContainerRef.current;

    if (container) {
      const newScrollTop =
        container.scrollTop + (event.deltaY > 0 ? scrollAmount : -scrollAmount);
      container.scrollTop = Math.max(
        0,
        Math.min(container.scrollHeight - container.clientHeight, newScrollTop)
      );
    }
  };

  return (
    <>
      <Modal
        show={showModalCloseChat}
        onHide={() => setShowModalCloseChat(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Atenção</Modal.Title>
        </Modal.Header>
        <Modal.Body>Tem certeza que deseja encerrar o chat?</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowModalCloseChat(false)}
          >
            Não
          </Button>
          <Button variant="primary" onClick={handleCloseChat}>
            Sim
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showModalTrasfAtendimento}
        onHide={() => setShowModalTrasfAtendimento(false)}
      >
        <Modal.Header>
          <Modal.Title className="text-center w-100 fw-bold text-secondary">
            Transferência de Chat
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ListUserStatus
            usuario={usuario}
            setUsuario={setUsuario}
            users={userStatus.filter(
              (usr) => usr.status !== enumUserStatus.Invisivel
            )}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowModalTrasfAtendimento(false)}
          >
            Fechar
          </Button>
          <Button
            variant="success"
            ref={bntTransferir}
            onClick={() => handleTransferChat()}
          >
            Transferir
          </Button>
        </Modal.Footer>
      </Modal>

      <div
        className="d-flex overflow-auto container "
        style={{ maxWidth: '900px' }}
      >
        <div className="border-0">
          {imageFile.length > 0 && <ImageListClipboard imageFile={imageFile} />}
        </div>
      </div>

      <Container
        className={`mt-2 ${
          dataClient?.seqStatusAtendimento !== atendimentoEmAndamento &&
          idSupportRequest > 0
            ? 'pe-none opacity-50'
            : ''
        }`}
      >
        {showPopupMsgPadrao && messagePadraoFiltro.length > 0 && (
          <div
            className="container"
            style={{
              maxHeight: '18rem',
              width: '71rem',
              zIndex: '1',
              position: 'absolute',
              bottom: '12rem',
              overflow: 'auto',
              padding: '0.8rem',
              boxShadow: '0px 0px 5px 0px rgba(0,0,0,0.75)',
              borderRadius: '11px',
              backgroundColor: 'white',
            }}
          >
            <ul className="list-unstyled m-0">
              {messagePadraoFiltro.map((item) => (
                <li
                  key={item.seqMensagemPadrao}
                  className="m-0 p-0"
                  style={{
                    boxShadow: '0px 0px 2px 0px rgba(0,0,0,0.3)',
                    borderRadius: '6px',
                  }}
                >
                  <div
                    role="button"
                    tabIndex={0}
                    className="p-3 mb-2"
                    onClick={() => handleClickItemMsgPadrao(item.texto)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleClickItemMsgPadrao(item.texto);
                      }
                    }}
                    style={{
                      cursor: 'pointer',
                      borderRadius: '6px',
                      backgroundColor: '#F0F8FF',
                      transition: 'background-color 0.3s ease', // Adiciona transição suave
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#dde4e8'; // Cor do bg-success
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#F0F8FF';
                    }}
                  >
                    <span
                      className="d-inline-block px-2 py-1 fw-bold bg-primary"
                      style={{
                        boxShadow: '0px 0px 5px 0px rgba(0,0,0,0.75)',
                        borderRadius: '6px',
                        color: 'white',
                        fontSize: '1.1rem',
                      }}
                    >
                      {item.atalho}
                      {item.identificacao}
                    </span>
                    <div
                      style={{ fontSize: '1rem' }}
                      dangerouslySetInnerHTML={{ __html: item.texto }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <Row
          className="justify-content-md-center ps-0 pe-0"
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
        >
          <div
            className=" overflow-auto send-message"
            ref={editorContainerRef}
            onWheel={handleWheel}
          >
            <Editor
              editorState={editorState}
              ref={messageInput}
              stripPastedStyles
              handlePastedText={handlePaste}
              toolbarClassName="bg-light"
              editorClassName={`form-control overflow-auto cls-editor-send-mensage ${
                editMessage.texto ? 'border-1 border-warning' : ''
              }
              ${citarMessage.texto ? 'border-1 border-success' : ''}`}
              handleReturn={handleReturn}
              onEditorStateChange={(ed) => {
                handleOnChange(ed);
              }}
              placeholder="Digite sua mensagem"
              toolbar={{
                options: ['inline'],
                inline: {
                  inDropdown: false,
                  options: ['bold', 'italic', 'underline'],
                  bold: {
                    icon: Boldicon,
                    className: 'rounded SendMessageIcon',
                  },
                  italic: {
                    icon: ItalicIcon,
                    className: 'rounded SendMessageIcon',
                  },
                  underline: {
                    icon: UnderlineIcon,
                    className: 'rounded SendMessageIcon',
                  },
                },
                colorPicker: {
                  className: 'bg-info rounded',
                  component: undefined,
                  popupClassName: undefined,
                },
                list: { inDropdown: false },
                textAlign: { inDropdown: false },
                link: {
                  inDropdown: false,
                  options: ['link', 'unlink'],
                  link: { className: 'bg-info rounded' },
                  unlink: { className: 'bg-info rounded' },
                },
                history: { inDropdown: false },
              }}
              toolbarCustomButtons={[
                <CustomOptionAnexo />,
                <CustomOptionChat />,
                <CustomLoadFile />,
              ]}
            />
          </div>

          <div className="d-flex w-100 mt-2 justify-content-between">
            <Button
              ref={bntSendMessage}
              hidden
              size="sm"
              variant={(() => {
                if (editMessage.texto) {
                  return 'warning text-white';
                }
                if (citarMessage.texto) {
                  return 'success';
                }
                return 'primary';
              })()}
              type="submit"
              className="w-50 me-2"
              disabled={false}
              onClick={() => {
                const textRaw = convertToRaw(editorState.getCurrentContent());
                for (let line = 0; line < textRaw.blocks.length; line += 1) {
                  textRaw.blocks[line].inlineStyleRanges = textRaw.blocks[
                    line
                  ].inlineStyleRanges.filter(
                    (itemStyle) =>
                      itemStyle.style === 'BOLD' ||
                      itemStyle.style === 'UNDERLINE' ||
                      itemStyle.style === 'ITALIC'
                  );
                }

                submitMessage(draftToHtml(textRaw));
              }}
            >
              {editMessage.texto && 'Editar Mensagem'}
              {citarMessage.texto && 'Citar Mensagem'}
              {!editMessage.texto && !citarMessage.texto && 'Enviar Mensagem'}
            </Button>
            <Form.Control
              type="file"
              ref={inputFile}
              className="visually-hidden"
              onChange={() => {
                handleSendFile();
              }}
            />
          </div>
        </Row>
      </Container>
    </>
  );
};

export default SendMessage;
